import { isNumber, isString } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';

import { Box, CircularProgress } from '@mui/material';

import usePageMeta from 'hooks/use-page-meta';
import Layout from 'pages/Search/components/Layout';
import Paginator from 'pages/Search/components/Paginator';
import SectionHeading from 'pages/Search/components/SectionHeading';
import VulnerabilitiesList from 'pages/Search/components/VulnerabilitiesList';
import { ROUTES } from 'pages/Search/constants';
import { formatVersion, normalizeName } from 'pages/Search/utils';
import { useCvesCatalog } from 'queries/catalog/use-cves-catalog';
import Utils from 'utils/Utils';

import { useProductCveCatalogPayload } from './hooks/use-product-cve-catalog-payload';
import useStyles from './styles';

const ProductCveList: React.FC = () => {
    const { classes } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [payload, actions] = useProductCveCatalogPayload();
    const { data, isLoading } = useCvesCatalog(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const vendorName = normalizeName(payload.vendor);
    const productName = normalizeName(payload.product);
    const fullProductName = isString(payload.version)
        ? `${productName}@${formatVersion(payload.version)}`
        : productName;
    const hasMetaPagination = payload.page > 1;

    usePageMeta({
        title: hasMetaPagination
            ? `${fullProductName} Security Vulnerabilities and Issues — Page ${payload.page} of ${fullProductName} CVE List | Vulners.com`
            : `${fullProductName} Security Vulnerabilities and Issues — ${fullProductName} CVE List | Vulners.com`,
        description: hasMetaPagination
            ? `${fullProductName} - Page ${payload.page}. ${fullProductName} vulnerabilities and security issues - all security problems and risks of ${fullProductName} in one place with links to detailed description`
            : `${fullProductName} vulnerabilities and security issues - all security problems and risks of ${fullProductName} in one place with links to detailed description`
    });

    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);
    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);

    const hasPagination = data ? data.total > payload.size : true;

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={[
                            vendorName,
                            productName,
                            isString(payload.version) ? formatVersion(payload.version) : undefined
                        ]}
                        breadcrumbs={[
                            { to: ROUTES.PRODUCTS(), label: 'Products' },
                            { to: ROUTES.VENDOR({ vendor: payload.vendor }), label: vendorName },
                            {
                                to: ROUTES.PRODUCT_VERSIONS({ vendor: payload.vendor, product: payload.product }),
                                label: productName
                            },
                            isNumber(payload.score)
                                ? {
                                      to: ROUTES.PRODUCT_VERSIONS({
                                          vendor: payload.vendor,
                                          product: payload.product,
                                          score: payload.score
                                      }),
                                      label: `CVSS Score ${payload.score} - ${payload.score + 1}`
                                  }
                                : null,
                            isNumber(payload.year)
                                ? {
                                      to: ROUTES.PRODUCT_VERSIONS({
                                          vendor: payload.vendor,
                                          product: payload.product,
                                          year: payload.year
                                      }),
                                      label: payload.year.toString()
                                  }
                                : null,
                            isNumber(payload.month)
                                ? {
                                      to: ROUTES.PRODUCT_VERSIONS({
                                          vendor: payload.vendor,
                                          product: payload.product,
                                          year: payload.year,
                                          month: payload.month
                                      }),
                                      label: Utils.monthOfYear(payload.month - 1)
                                  }
                                : null,
                            isString(payload.version)
                                ? {
                                      to: ROUTES.PRODUCT_CVES({
                                          vendor: payload.vendor,
                                          product: payload.product,
                                          year: payload.year,
                                          month: payload.month,
                                          score: payload.score,
                                          version: formatVersion(payload.version)
                                      }),
                                      label: formatVersion(payload.version)
                                  }
                                : null
                        ]}
                    />
                    {isLoading ? (
                        <Box className={classes.spinner}>
                            <CircularProgress />
                        </Box>
                    ) : data ? (
                        <>
                            <VulnerabilitiesList total={data.total} items={data.items} />
                            {hasPagination ? (
                                <Paginator
                                    totalCountLabel={'Total number of security vulnerabilities'}
                                    page={payload.page}
                                    rowsPerPage={payload.size}
                                    rowsPerPageOptions={null}
                                    count={data.total}
                                    onPageChange={handlePageChange}
                                    getPaginationHref={getPaginationHref}
                                />
                            ) : null}
                        </>
                    ) : null}
                </div>
            }
        />
    );
};

export default ProductCveList;
